import { API_BASE_URL, TOKEN_STORAGE_KEY, USER_STORAGE_KEY } from '../config/index.js';

/** Build a `?a=1&b=2` string from a params object, skipping empty values. */
function queryString(params = {}) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '' && !(k === 'stage' && v === 'all')) qs.set(k, v);
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
}

// Auth-flow endpoints where a 401 is a normal outcome (bad credentials, wrong
// OTP, invalid reset code, stale token on the session check). These must NOT
// trigger the expired-session redirect or the login flow's inline error
// display would break (and /auth/me is handled by AuthContext itself).
const AUTH_ENDPOINT_PREFIXES = [
  '/auth/login',
  '/auth/register',
  '/auth/mfa/',
  '/auth/forgot-password/',
  '/auth/me',
  '/auth/supertokens/',
  // An expired or reused social sign-in ticket is a normal 401 on the
  // /social-callback page, not a dead session.
  '/auth/social/complete',
  '/connectors/register',
  // Mobile OTP verification answers a wrong or expired CODE with 401, which is
  // a normal outcome of typing six digits — not a dead session. Without this,
  // one mistyped digit ran the expired-session handler and signed the user out
  // mid-onboarding, losing the step they were on (found while building QA 8024;
  // the Connector Profile flow hits the same endpoint).
  '/connectors/me/mobile/',
];

function isAuthEndpoint(endpoint) {
  return AUTH_ENDPOINT_PREFIXES.some((p) => endpoint.startsWith(p));
}

// The stored session went stale mid-use: clear it and bounce to login with a
// flag so the login page can explain why the user was signed out.
function handleSessionExpired() {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(USER_STORAGE_KEY);
  sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  sessionStorage.removeItem(USER_STORAGE_KEY);
  if (!window.location.pathname.startsWith('/login')) {
    window.location.assign('/login?expired=1');
  }
}

/**
 * Thin fetch wrapper around the backend API.
 *
 * - Attaches the stored Bearer token automatically.
 * - Unwraps the backend's `{ data }` envelope on success.
 * - Throws an Error carrying { status, details } on failure, so callers can
 *   `try/catch` and show err.message.
 * - A 401 on a non-auth endpoint while a session exists means the session
 *   expired: the stored session is cleared and the user is sent to login.
 */
class ApiService {
  async request(endpoint, { method = 'GET', body, headers } = {}) {
    // "Keep me signed in" persists to localStorage; a session-only login uses
    // sessionStorage. Read whichever holds the token.
    const token =
      localStorage.getItem(TOKEN_STORAGE_KEY) ?? sessionStorage.getItem(TOKEN_STORAGE_KEY);

    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : null;

    if (!res.ok) {
      // Central expired-session handling: only when the user actually had a
      // session (token present) and this isn't part of the auth flow itself.
      if (res.status === 401 && token && !isAuthEndpoint(endpoint)) {
        handleSessionExpired();
      }
      const message = payload?.error?.message || `Request failed (${res.status})`;
      const error = new Error(message);
      error.status = res.status;
      error.details = payload?.error?.details;
      throw error;
    }

    return payload?.data ?? payload;
  }

  // ---- Auth ----
  register({ email, name, password }) {
    return this.request('/auth/register', { method: 'POST', body: { email, name, password } });
  }

  /**
   * Sign in with an email OR mobile number. Resolves to either a completed
   * session ({ user, token }) or an MFA challenge ({ mfaRequired, challengeId }).
   */
  login({ identity, password, remember = false, turnstileToken }) {
    return this.request('/auth/login', {
      method: 'POST',
      body: { identity, password, remember, turnstileToken },
    });
  }

  verifyMfa({ challengeId, code }) {
    return this.request('/auth/mfa/verify', { method: 'POST', body: { challengeId, code } });
  }

  // ---- Password reset / account recovery ----
  requestPasswordReset({ identity, method, turnstileToken }) {
    return this.request('/auth/forgot-password/request', {
      method: 'POST',
      body: { identity, method, turnstileToken },
    });
  }

  // Challenge-only, and deliberately NOT a repost of the request form: that
  // endpoint is Turnstile-gated and the widget is off screen by this step, so
  // resending through it replayed a spent single-use token (QA 8425).
  // `method` is echoed back from step 1 so the resend uses the channel the
  // user chose; the challenge itself does not carry it (QA 8552).
  resendPasswordReset({ challengeId, method }) {
    return this.request('/auth/forgot-password/resend', {
      method: 'POST',
      body: { challengeId, method },
    });
  }

  verifyResetCode({ challengeId, code }) {
    return this.request('/auth/forgot-password/verify', { method: 'POST', body: { challengeId, code } });
  }

  resetPassword({ resetToken, password }) {
    return this.request('/auth/forgot-password/reset', { method: 'POST', body: { resetToken, password } });
  }

  resendMfa({ challengeId }) {
    return this.request('/auth/mfa/resend', { method: 'POST', body: { challengeId } });
  }

  me() {
    return this.request('/auth/me');
  }

  // ---- Users ----
  listUsers({ limit = 50, offset = 0 } = {}) {
    return this.request(`/users?limit=${limit}&offset=${offset}`);
  }

  // ---- Audit (admin) ----
  listAuditLogs({ limit = 50, offset = 0 } = {}) {
    return this.request(`/auth/audit-logs?limit=${limit}&offset=${offset}`);
  }

  // ---- Connector self-registration ----
  registerConnector({ method, contact, password, referralSource, referredBy, turnstileToken }) {
    return this.request('/connectors/register', {
      method: 'POST',
      body: { method, contact, password, referralSource, referredBy, turnstileToken },
    });
  }

  verifyRegistration({ challengeId, code }) {
    return this.request('/connectors/register/verify', {
      method: 'POST',
      body: { challengeId, code },
    });
  }

  resendRegistration({ challengeId }) {
    return this.request('/connectors/register/resend', { method: 'POST', body: { challengeId } });
  }

  // ---- Connector onboarding + profile ----
  getConnectorOnboarding() {
    return this.request('/connectors/me/onboarding');
  }

  saveConnectorOnboarding(payload) {
    return this.request('/connectors/me/onboarding', { method: 'PUT', body: payload });
  }

  submitConnectorOnboarding() {
    return this.request('/connectors/me/onboarding/submit', { method: 'POST' });
  }

  getConnectorProfile() {
    return this.request('/connectors/me');
  }

  updateConnectorProfile(payload) {
    return this.request('/connectors/me/profile', { method: 'PUT', body: payload });
  }

  updateConnectorPayout(payload) {
    return this.request('/connectors/me/payout', { method: 'PUT', body: payload });
  }

  challengeConnectorMobile({ mobile } = {}) {
    return this.request('/connectors/me/mobile/challenge', { method: 'POST', body: { mobile } });
  }

  verifyConnectorMobile({ challengeId, code, mobile }) {
    return this.request('/connectors/me/mobile/verify', {
      method: 'POST',
      body: { challengeId, code, mobile },
    });
  }

  // ---- Admin: accreditation transitions ----
  updateConnectorAccreditation({ userId, status }) {
    return this.request(`/connectors/${userId}/accreditation`, { method: 'PUT', body: { status } });
  }

  // ---- Compliance / Accreditation Management (UC adeb965c) ----
  listConnectors(params = {}) {
    return this.request(`/connectors${queryString(params)}`);
  }

  getConnectorReview(userId) {
    return this.request(`/connectors/${userId}`);
  }

  getConnectorAudit(userId) {
    return this.request(`/connectors/${userId}/audit`);
  }

  correctConnector(userId, payload) {
    return this.request(`/connectors/${userId}/correction`, { method: 'PUT', body: payload });
  }

  replaceConnectorKyc(userId, { document, fileName, mimeType, fileRef }) {
    return this.request(`/connectors/${userId}/kyc${this._tenantQ()}`, {
      method: 'POST',
      body: { document, fileName, mimeType, fileRef },
    });
  }

  decideConnector(userId, { action, comment }) {
    return this.request(`/connectors/${userId}/decision`, { method: 'PUT', body: { action, comment } });
  }

  // ---- Referrals (New Referral Wizard, UC 52b753e9) ----
  listReferrals() {
    return this.request('/referrals');
  }

  getReferral(id) {
    return this.request(`/referrals/${id}`);
  }

  createReferralDraft(payload) {
    return this.request('/referrals', { method: 'POST', body: payload });
  }

  updateReferralDraft(id, payload) {
    return this.request(`/referrals/${id}`, { method: 'PUT', body: payload });
  }

  submitReferral(id, payload = {}) {
    return this.request(`/referrals/${id}/submit`, { method: 'POST', body: payload });
  }

  // ---- Referral supporting documents (QA 7749) ----
  listReferralDocuments(id) {
    return this.request(`/referrals/${id}/documents`);
  }

  uploadReferralDocument(id, { fileName, mimeType, sizeBytes, fileRef }) {
    return this.request(`/referrals/${id}/documents`, {
      method: 'POST',
      body: { fileName, mimeType, sizeBytes, fileRef },
    });
  }

  deleteReferralDocument(id, docId) {
    return this.request(`/referrals/${id}/documents/${docId}`, { method: 'DELETE' });
  }

  getIntakeDocument(id, docId) {
    return this.request(`/referrals/intake/${id}/documents/${docId}`);
  }

  /**
   * Every supporting document for a referral as one zip (QA 7962).
   * `request()` always parses JSON, so this goes direct to fetch and returns a
   * Blob; the error shape is kept identical so callers handle it the same way.
   */
  async downloadIntakeDocumentsZip(id) {
    const token =
      localStorage.getItem(TOKEN_STORAGE_KEY) ?? sessionStorage.getItem(TOKEN_STORAGE_KEY);
    const res = await fetch(`${API_BASE_URL}/referrals/intake/${id}/documents-zip`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      if (res.status === 401 && token) handleSessionExpired();
      const payload = res.headers.get('content-type')?.includes('application/json')
        ? await res.json().catch(() => null)
        : null;
      const error = new Error(payload?.error?.message || `Request failed (${res.status})`);
      error.status = res.status;
      throw error;
    }
    return res.blob();
  }

  // ---- Account Management intake (UC 8321b657) ----
  getReferralBranches() {
    return this.request('/referrals/branches');
  }

  getReferralProducts() {
    return this.request('/referrals/products');
  }

  listIntakeQueue(params = {}) {
    return this.request(`/referrals/intake/queue${queryString(params)}`);
  }

  getIntakeReferral(id) {
    return this.request(`/referrals/intake/${id}`);
  }

  screenReferral(id) {
    return this.request(`/referrals/intake/${id}/screen`, { method: 'POST' });
  }

  recordScreeningOutcome(id, outcome) {
    return this.request(`/referrals/intake/${id}/outcome`, { method: 'PUT', body: { outcome } });
  }

  routeReferral(id, { branch, remark }) {
    return this.request(`/referrals/intake/${id}/route`, { method: 'PUT', body: { branch, remark } });
  }

  // ---- Branch Operations (UC 5ffc6691) ----
  listBranchQueue(params = {}) {
    return this.request(`/referrals/intake/branch-queue${queryString(params)}`);
  }

  getRelationshipManagers() {
    return this.request('/referrals/relationship-managers');
  }

  confirmBranchReceipt(id) {
    return this.request(`/referrals/intake/${id}/confirm-receipt`, { method: 'POST' });
  }

  /** RM + Branch Operations user takes a waiting referral into their own pipeline (QA 8562). */
  claimReferral(id) {
    return this.request(`/referrals/intake/${id}/claim`, { method: 'POST' });
  }

  assignRm(id, rm) {
    return this.request(`/referrals/intake/${id}/assign-rm`, { method: 'PUT', body: { rm } });
  }

  // ---- Relationship Manager pipeline (UC 8271671d) ----
  listRmQueue(params = {}) {
    return this.request(`/referrals/intake/rm-queue${queryString(params)}`);
  }

  getMilestones() {
    return this.request('/referrals/milestones');
  }

  updateMilestone(id, { milestone, approvedAmount, effectiveDate }) {
    return this.request(`/referrals/intake/${id}/milestone`, { method: 'PUT', body: { milestone, approvedAmount, effectiveDate } });
  }

  toggleHold(id, { onHold, reason }) {
    return this.request(`/referrals/intake/${id}/hold`, { method: 'PUT', body: { onHold, reason } });
  }

  addReferralRemark(id, remark) {
    return this.request(`/referrals/intake/${id}/remark`, { method: 'POST', body: { remark } });
  }

  // ---- Commission configuration (UC 3022289b) ----
  listCommissionProducts() {
    return this.request('/commission/products');
  }

  createCommissionProduct({ name, category, baseRate, commissionType, fixedAmount }) {
    return this.request('/commission/products', { method: 'POST', body: { name, category, baseRate, commissionType, fixedAmount } });
  }

  publishCommission(changes) {
    return this.request('/commission/publish', { method: 'PUT', body: { changes } });
  }

  // ---- Commission processing (UC cfa05622) ----
  listCommissionRecords(params = {}) {
    return this.request(`/commission/records${queryString(params)}`);
  }

  getCommissionRecord(id) {
    return this.request(`/commission/records/${id}`);
  }

  listCommissionRecordConnectors() {
    return this.request('/commission/records/connectors');
  }

  overrideCommission(id, { grossOverride, ratePercent, reason }) {
    return this.request(`/commission/records/${id}/override`, { method: 'PUT', body: { grossOverride, ratePercent, reason } });
  }

  reassignCommission(id, { connectorId, reason }) {
    return this.request(`/commission/records/${id}/reassign`, { method: 'PUT', body: { connectorId, reason } });
  }

  holdCommission(id, { onHold, reason }) {
    return this.request(`/commission/records/${id}/hold`, { method: 'PUT', body: { onHold, reason } });
  }

  finalizeCommission(id) {
    return this.request(`/commission/records/${id}/finalize`, { method: 'POST' });
  }

  // ---- Connector: my earnings summary (net earned / pending / last payout) ----
  myCommissionSummary() {
    return this.request('/commission/me/summary');
  }

  // ---- Finance export & handoff (UC fb271b17) ----
  listExportPending() {
    return this.request('/commission/export/pending');
  }

  listExportExcluded() {
    return this.request('/commission/export/excluded');
  }

  listExportHistory({ limit = 10, offset = 0 } = {}) {
    return this.request(`/commission/export/history?limit=${limit}&offset=${offset}`);
  }

  getExportKpis() {
    return this.request('/commission/export/kpis');
  }

  compileExport(recordIds) {
    return this.request('/commission/export/compile', { method: 'POST', body: { recordIds } });
  }

  releaseExport(batchId, memo) {
    return this.request(`/commission/export/${batchId}/release`, { method: 'PUT', body: { memo } });
  }

  downloadExport(batchId) {
    return this.request(`/commission/export/${batchId}/download`);
  }

  // AES-256 encrypted zip + one-time password (returned once, never stored).
  downloadExportEncrypted(batchId) {
    return this.request(`/commission/export/${batchId}/download-encrypted`, { method: 'POST' });
  }

  // ---- Group Admin: user administration (UC f7b0ec93) ----
  listInternalUsers({ search = '', status = 'all', role = 'all', limit, offset } = {}) {
    const qs = new URLSearchParams();
    if (search) qs.set('search', search);
    if (status && status !== 'all') qs.set('status', status);
    if (role && role !== 'all') qs.set('role', role);
    if (limit != null) qs.set('limit', limit);
    if (offset != null) qs.set('offset', offset);
    const q = qs.toString();
    return this.request(`/admin/users${q ? `?${q}` : ''}`);
  }

  provisionInternalUser({ name, email, role, secondaryRole = null }) {
    return this.request('/admin/users', { method: 'POST', body: { name, email, role, secondaryRole } });
  }

  setInternalUserStatus(id, status) {
    return this.request(`/admin/users/${id}/status`, { method: 'PUT', body: { status } });
  }

  resetInternalUserPassword(id) {
    return this.request(`/admin/users/${id}/reset-password`, { method: 'POST' });
  }

  updateInternalUser(id, { name, role, secondaryRole, branches, password }) {
    return this.request(`/admin/users/${id}`, { method: 'PUT', body: { name, role, secondaryRole, branches, password } });
  }

  getUserBranches(id) {
    return this.request(`/admin/users/${id}/branches`);
  }

  // ---- Group Admin: Relationship Manager ↔ branch mapping ----
  listRelationshipManagers() {
    return this.request('/admin/relationship-managers');
  }

  createRelationshipManager({ name, branches, active }) {
    return this.request('/admin/relationship-managers', { method: 'POST', body: { name, branches, active } });
  }

  updateRelationshipManager(id, payload) {
    return this.request(`/admin/relationship-managers/${id}`, { method: 'PUT', body: payload });
  }

  deleteRelationshipManager(id) {
    return this.request(`/admin/relationship-managers/${id}`, { method: 'DELETE' });
  }

  // ---- Admin: Branch registry + user tagging ----
  listBranches() {
    return this.request('/admin/branches');
  }

  createBranch({ name, code, region, active }) {
    return this.request('/admin/branches', { method: 'POST', body: { name, code, region, active } });
  }

  updateBranch(id, payload) {
    return this.request(`/admin/branches/${id}`, { method: 'PUT', body: payload });
  }

  deleteBranch(id) {
    return this.request(`/admin/branches/${id}`, { method: 'DELETE' });
  }

  setBranchMembers(id, members) {
    return this.request(`/admin/branches/${id}/members`, { method: 'PUT', body: { members } });
  }

  listTaggableUsers() {
    return this.request('/admin/branches/taggable-users');
  }

  // ---- Group Admin: role & permission governance ----
  listRoles() {
    return this.request('/admin/roles');
  }

  getRoleMatrix(key) {
    return this.request(`/admin/roles/${key}/matrix`);
  }

  saveRoleMatrix(key, grants) {
    return this.request(`/admin/roles/${key}/matrix`, { method: 'PUT', body: { grants } });
  }

  // ---- Group Admin: security & audit oversight ----
  listSecurityAuditLogs({ severity = 'all', category = 'all', operatorRole = 'all', search = '' } = {}) {
    const qs = new URLSearchParams();
    if (severity && severity !== 'all') qs.set('severity', severity);
    if (category && category !== 'all') qs.set('category', category);
    if (operatorRole && operatorRole !== 'all') qs.set('operatorRole', operatorRole);
    if (search) qs.set('search', search);
    const q = qs.toString();
    return this.request(`/admin/security/audit-logs${q ? `?${q}` : ''}`);
  }

  getSecurityMetrics() {
    return this.request('/admin/security/metrics');
  }

  listLockouts() {
    return this.request('/admin/security/lockouts');
  }

  investigateIncident(userId, { memo }) {
    return this.request(`/admin/security/investigate/${userId}`, { method: 'POST', body: { memo } });
  }

  liftPolicySuspension(userId) {
    return this.request(`/admin/security/suspensions/${userId}/lift-policy`, { method: 'POST' });
  }

  releaseLockout(id) {
    return this.request(`/admin/security/lockouts/${id}/release`, { method: 'POST' });
  }

  exportAuditPdf() {
    return this.request('/admin/security/export');
  }

  // ---- Group Admin: notification management (UC 6e67a72d) ----
  listNotificationRules() {
    return this.request('/admin/notifications/rules');
  }

  updateNotificationRule(id, payload) {
    return this.request(`/admin/notifications/rules/${id}`, { method: 'PUT', body: payload });
  }

  toggleNotificationRule(id, enabled) {
    return this.request(`/admin/notifications/rules/${id}/toggle`, { method: 'PATCH', body: { enabled } });
  }

  listNotificationLogs({ category = 'all', status = 'all', search = '', from = '', to = '' } = {}) {
    const qs = new URLSearchParams();
    if (category && category !== 'all') qs.set('category', category);
    if (status && status !== 'all') qs.set('status', status);
    if (search) qs.set('search', search);
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    const q = qs.toString();
    return this.request(`/admin/notifications/logs${q ? `?${q}` : ''}`);
  }

  resendNotification(id) {
    return this.request(`/admin/notifications/logs/${id}/resend`, { method: 'POST' });
  }

  // ---- In-app topbar notifications (QA 7747/7750/7759) ----
  myNotifications() {
    return this.request('/admin/notifications/me');
  }

  markNotificationsRead() {
    return this.request('/admin/notifications/me/read', { method: 'POST' });
  }

  // ---- Credit checks (CIC / PH bureaus via pluggable adapter) ----
  runCreditCheck({ referralId, subjectName, subjectRef }) {
    const tenant = (typeof localStorage !== 'undefined' && localStorage.getItem('afc_tenant')) || '';
    const q = tenant ? `?tenant=${encodeURIComponent(tenant)}` : '';
    return this.request(`/credit/checks${q}`, { method: 'POST', body: { referralId, subjectName, subjectRef } });
  }

  listCreditChecks(referralId) {
    return this.request(`/credit/referrals/${referralId}`);
  }

  // ---- KYC documents + OCR ----
  uploadKycDocument(docType, body) {
    const tenant = (typeof localStorage !== 'undefined' && localStorage.getItem('afc_tenant')) || '';
    const q = tenant ? `?tenant=${encodeURIComponent(tenant)}` : '';
    return this.request(`/kyc/me/${docType}${q}`, { method: 'POST', body });
  }

  listMyKyc() {
    return this.request('/kyc/me');
  }

  // The connector's OWN document image → { dataUrl, docType, fileName, ... }.
  // Ownership is enforced server-side; no tenant query, since your own document
  // is yours regardless of the company it was submitted for.
  getMyKycImage(id) {
    return this.request(`/kyc/me/${id}/image`);
  }

  _tenantQ() {
    const tenant = (typeof localStorage !== 'undefined' && localStorage.getItem('afc_tenant')) || '';
    return tenant ? `?tenant=${encodeURIComponent(tenant)}` : '';
  }

  // Compliance: a connector's KYC docs (ids, types, extracted fields, status).
  listConnectorKyc(userId) {
    return this.request(`/kyc/connectors/${userId}${this._tenantQ()}`);
  }

  // Compliance: decrypt-on-demand image for review → { dataUrl, docType, ... }.
  getKycImage(id) {
    return this.request(`/kyc/${id}/image${this._tenantQ()}`);
  }

  // Compliance: run the IDMeta eKYC trust flow for a connector.
  runEkyc(userId) {
    return this.request(`/kyc/connectors/${userId}/ekyc${this._tenantQ()}`, { method: 'POST' });
  }

  // ---- SuperTokens-verified sign-in (credentials checked by the ST core) ----
  loginViaSupertokens({ identity, password }) {
    return this.request('/auth/supertokens/login', { method: 'POST', body: { identity, password } });
  }

  // ---- Super Admin group console (leaderboard, CXO report, blacklist, viewers) ----
  superadminOverview() {
    return this.request('/superadmin/overview');
  }

  superadminLeaderboard(params = {}) {
    return this.request(`/superadmin/connectors${queryString(params)}`);
  }

  superadminReport(uid, company) {
    return this.request(`/superadmin/connectors/${encodeURIComponent(uid)}/report${company && company !== 'all' ? `?company=${encodeURIComponent(company)}` : ''}`);
  }

  superadminSetBlacklist(uid, { blacklisted, reason }) {
    return this.request(`/superadmin/connectors/${encodeURIComponent(uid)}/blacklist`, {
      method: 'POST',
      body: { blacklisted, reason },
    });
  }

  listViewers() {
    return this.request('/superadmin/viewers');
  }

  addViewer({ name, email, password }) {
    return this.request('/superadmin/viewers', { method: 'POST', body: { name, email, password } });
  }

  removeViewer(id) {
    return this.request(`/superadmin/viewers/${id}`, { method: 'DELETE' });
  }

  // ---- Tenant branding (self-serve) ----
  // tenantSlug is a dev hint so the backend resolves the tenant on localhost; in
  // production the request Host identifies the company and the hint is ignored.
  getTenantBranding(tenantSlug) {
    const q = tenantSlug ? `?tenant=${encodeURIComponent(tenantSlug)}` : '';
    return this.request(`/tenant/branding${q}`);
  }

  updateTenantBranding(tenantSlug, body) {
    const q = tenantSlug ? `?tenant=${encodeURIComponent(tenantSlug)}` : '';
    return this.request(`/tenant/branding${q}`, { method: 'PUT', body });
  }

  // ---- Integrations (external API switches + credentials; System Admin) ----
  getIntegrations() {
    return this.request('/integrations');
  }

  /** body: { provider, config } — secret fields left '' keep the stored value; null clears. */
  saveIntegration(key, body) {
    return this.request(`/integrations/${encodeURIComponent(key)}`, { method: 'PUT', body });
  }

  /** Drop the saved row so the integration falls back to the server's .env. */
  resetIntegration(key) {
    return this.request(`/integrations/${encodeURIComponent(key)}`, { method: 'DELETE' });
  }

  /** Exercise the currently EFFECTIVE settings. `to` is the test destination for delivery providers. */
  testIntegration(key, body = {}) {
    return this.request(`/integrations/${encodeURIComponent(key)}/test`, { method: 'POST', body });
  }

  // ---- Social sign-in (Google / Facebook, connectors only — 8207) ----
  /** Begin a provider round trip; resolves to { url } to send the browser to. */
  socialStart(provider) {
    return this.request(`/auth/social/${encodeURIComponent(provider)}/start`, { method: 'POST', body: {} });
  }

  /** Signed-in connector links a provider (password re-confirmed); resolves to { url }. */
  socialLink(provider, password) {
    return this.request(`/auth/social/${encodeURIComponent(provider)}/link`, { method: 'POST', body: { password } });
  }

  /** Trade the one-time ticket from /social-callback for { user, token, created }. */
  socialComplete(ticket) {
    return this.request('/auth/social/complete', { method: 'POST', body: { ticket } });
  }

  socialIdentities() {
    return this.request('/auth/social/identities');
  }

  socialUnlink(provider) {
    return this.request(`/auth/social/identities/${encodeURIComponent(provider)}`, { method: 'DELETE' });
  }

  // ---- Management reports (SME Concern #18) ----
  // `months` is a list of 1-12 (QA 8145). An empty list means the whole year,
  // matching the server: "Select All" and "nothing ticked" are the same period.
  getTopConnectorsReport(year, months = []) {
    const m = months.length ? `&months=${encodeURIComponent(months.join(','))}` : '';
    return this.request(`/reports/top-connectors?year=${encodeURIComponent(year)}${m}`);
  }

  // Detailed Connector and Borrower Extract (CR 8661).
  getDetailedExtract(year, months = []) {
    const m = months.length ? `&months=${encodeURIComponent(months.join(','))}` : '';
    return this.request(`/reports/extract?year=${encodeURIComponent(year)}${m}`);
  }

  getConnectorReferralsReport(userId, { year, months = [], from, to } = {}) {
    const params = new URLSearchParams({ year: String(year) });
    if (months.length) params.set('months', months.join(','));
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    return this.request(`/reports/connectors/${encodeURIComponent(userId)}/referrals?${params}`);
  }
}

export default new ApiService();
